import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { calculateSalesTax } from '../utils/salesTax';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CartContext = createContext();

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};

// Helper function to safely parse cart from localStorage
const getStoredCart = () => {
  try {
    const savedCart = localStorage.getItem('engraveca_cart');
    if (savedCart) {
      const parsed = JSON.parse(savedCart);
      // Validate that parsed data is an array
      if (Array.isArray(parsed)) {
        return parsed;
      }
    }
  } catch (error) {
    console.error('Error parsing cart from localStorage:', error);
    // Clear corrupted data
    localStorage.removeItem('engraveca_cart');
  }
  return [];
};

export const CartProvider = ({ children }) => {
  const [cartItems, setCartItems] = useState(getStoredCart);
  // Mark as hydrated after first render - using layout effect to avoid flash
  const [isHydrated, setIsHydrated] = useState(() => {
    // Check if already in browser environment
    return typeof window !== 'undefined';
  });
  
  // Shipping/tax related state
  const [shippingZip, setShippingZip] = useState(() => {
    try {
      return localStorage.getItem('engraveca_shipping_zip') || '';
    } catch {
      return '';
    }
  });
  
  const [isTaxExempt, setIsTaxExempt] = useState(() => {
    try {
      return localStorage.getItem('engraveca_tax_exempt') === 'true';
    } catch {
      return false;
    }
  });
  
  const [taxOverride, setTaxOverride] = useState(null); // For admin override

  // Mark hydration complete on mount if not already
  useEffect(() => {
    if (!isHydrated) {
      setIsHydrated(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Persist cart to localStorage - only after hydration to prevent overwriting with empty array
  useEffect(() => {
    if (isHydrated) {
      try {
        localStorage.setItem('engraveca_cart', JSON.stringify(cartItems));
      } catch (error) {
        console.error('Error saving cart to localStorage:', error);
      }
    }
  }, [cartItems, isHydrated]);
  
  // Persist shipping ZIP
  useEffect(() => {
    if (isHydrated) {
      try {
        localStorage.setItem('engraveca_shipping_zip', shippingZip);
      } catch (error) {
        console.error('Error saving shipping ZIP to localStorage:', error);
      }
    }
  }, [shippingZip, isHydrated]);
  
  // Persist tax exempt status
  useEffect(() => {
    if (isHydrated) {
      try {
        localStorage.setItem('engraveca_tax_exempt', isTaxExempt.toString());
      } catch (error) {
        console.error('Error saving tax exempt status to localStorage:', error);
      }
    }
  }, [isTaxExempt, isHydrated]);

  const addToCart = (product, selectedOptions, quantity = 1) => {
    // Calculate price based on product's pricing object or use basePrice/retail_price
    let itemPrice = product.basePrice || product.retail_price || 0;
    if (product.pricing && product.pricing[selectedOptions.quantity]) {
      itemPrice = product.pricing[selectedOptions.quantity];
    }
    
    // Determine the section/category for free shipping calculation
    const section = product.section || (product.category?.includes('swag') ? 'swag' : 
                    product.pricing_type === 'per_sqft' || product.priceType === 'sqft' || product.priceType === 'fixed' || product.product_type === 'LGF' ? 'largeformat' : 'smallformat');
    
    // Get image URL - handle both image and image_url fields, and local paths
    let imageUrl = product.image || product.image_url || (product.images && product.images[0]) || null;
    if (imageUrl && imageUrl.startsWith('/') && !imageUrl.startsWith('//')) {
      imageUrl = `${API_URL}${imageUrl}`;
    }
    
    const cartItem = {
      id: `${product.id}-${Date.now()}`,
      productId: product.id,
      name: product.name,
      image: imageUrl,
      sku: product.sku,
      basePrice: itemPrice,
      selectedOptions,
      quantity,
      total: itemPrice * quantity,
      section: section
    };

    setCartItems(prev => [...prev, cartItem]);
  };

  const removeFromCart = (itemId) => {
    setCartItems(prev => prev.filter(item => item.id !== itemId));
  };

  const updateQuantity = (itemId, newQuantity) => {
    if (newQuantity < 1) return;
    
    setCartItems(prev => prev.map(item => {
      if (item.id === itemId) {
        return {
          ...item,
          quantity: newQuantity,
          total: item.basePrice * newQuantity
        };
      }
      return item;
    }));
  };

  const clearCart = () => {
    setCartItems([]);
    setShippingZip('');
    setTaxOverride(null);
  };
  
  // Update shipping ZIP code
  const updateShippingZip = useCallback((zip) => {
    setShippingZip(zip);
    setTaxOverride(null); // Reset any override when ZIP changes
  }, []);
  
  // Set tax exempt status
  const updateTaxExempt = useCallback((exempt) => {
    setIsTaxExempt(exempt);
  }, []);
  
  // Admin: Override tax calculation
  const setManualTaxOverride = useCallback((taxAmount) => {
    setTaxOverride(taxAmount);
  }, []);
  
  // Clear tax override
  const clearTaxOverride = useCallback(() => {
    setTaxOverride(null);
  }, []);

  // ===== Promotions State =====
  const [couponCode, setCouponCode] = useState('');
  const [appliedPromos, setAppliedPromos] = useState([]);
  const [promoMessages, setPromoMessages] = useState([]);
  const [unlockMessages, setUnlockMessages] = useState([]);
  const [promoLoading, setPromoLoading] = useState(false);
  const [discountAmount, setDiscountAmount] = useState(0);
  const [promoFreeShipping, setPromoFreeShipping] = useState(false);

  // Calculate cart totals
  const cartTotal = cartItems.reduce((sum, item) => sum + item.total, 0);
  const cartCount = cartItems.length;
  
  // Fetch promotions from API when cart changes
  const fetchPromoCalculation = useCallback(async (code = null) => {
    if (cartItems.length === 0) {
      setAppliedPromos([]);
      setPromoMessages([]);
      setUnlockMessages([]);
      setDiscountAmount(0);
      setPromoFreeShipping(false);
      return;
    }
    
    setPromoLoading(true);
    try {
      const cartData = {
        items: cartItems.map(item => ({
          product_id: item.productId,
          name: item.name,
          category: item.category || '',
          section: item.section || 'smallformat',
          quantity: item.quantity,
          unit_price: item.basePrice,
          is_sale_item: item.isSaleItem || false
        })),
        coupon_code: code || couponCode || null,
        shipping_zip: shippingZip || null
      };
      
      const res = await fetch(`${API_URL}/api/promotions/calculate-cart`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(cartData)
      });
      
      if (res.ok) {
        const data = await res.json();
        setAppliedPromos(data.applied_promos || []);
        setPromoMessages(data.messages || []);
        setUnlockMessages(data.unlock_messages || []);
        setDiscountAmount(data.discount_amount || 0);
        setPromoFreeShipping(data.has_free_shipping || false);
      }
    } catch (error) {
      console.error('Error fetching promo calculation:', error);
    }
    setPromoLoading(false);
  }, [cartItems, couponCode, shippingZip]);

  // Fetch promos when cart changes
  useEffect(() => {
    if (isHydrated && cartItems.length > 0) {
      fetchPromoCalculation();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cartItems, isHydrated]);

  // Apply coupon code
  const applyCoupon = useCallback(async (code) => {
    setCouponCode(code);
    await fetchPromoCalculation(code);
  }, [fetchPromoCalculation]);

  // Remove coupon
  const removeCoupon = useCallback(() => {
    setCouponCode('');
    fetchPromoCalculation(null);
  }, [fetchPromoCalculation]);
  
  // Shipping cost (now driven by promos)
  const hasFreeShipping = promoFreeShipping;
  const shippingCost = hasFreeShipping ? 0 : 15.00;
  
  // Calculate sales tax based on shipping ZIP
  const taxCalculation = calculateSalesTax(cartTotal - discountAmount, shippingZip, isTaxExempt);
  
  // Use override if set, otherwise use calculated tax
  const taxAmount = taxOverride !== null ? taxOverride : taxCalculation.taxAmount;
  
  // Calculate final total
  const orderTotal = (cartTotal - discountAmount) + shippingCost + taxAmount;

  return (
    <CartContext.Provider value={{
      // Cart items
      cartItems,
      addToCart,
      removeFromCart,
      updateQuantity,
      clearCart,
      
      // Totals
      cartTotal,
      cartCount,
      orderTotal,
      discountAmount,
      
      // Shipping
      shippingCost,
      hasFreeShipping,
      
      // Promotions
      couponCode,
      applyCoupon,
      removeCoupon,
      appliedPromos,
      promoMessages,
      unlockMessages,
      promoLoading,
      
      // Tax
      shippingZip,
      updateShippingZip,
      taxCalculation,
      taxAmount,
      isTaxExempt,
      updateTaxExempt,
      
      // Admin controls
      taxOverride,
      setManualTaxOverride,
      clearTaxOverride,
    }}>
      {children}
    </CartContext.Provider>
  );
};
